"use client";

import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { defaultTo, get } from "lodash-es";
import { useCallback } from "react";
import { useTranslations, useLocale } from "use-intl";

import { ErrorIcon, PersonUnavailableIcon } from "@/components/appIcons";
import { FeaturePageBacklinks } from "@/components/feature-page-backlinks/feature-page-backlinks";
import { HeroBackgroundImage } from "@/components/hero-background-image";
import { Link } from "@/components/link";
import { AlertsCallout } from "@/components/messaging/alerts-callout";
import { Spinner } from "@/components/ui/spinner";
import { ARTIST_DISCOGRAPHY_SURFACE } from "@/facets/config";
import { useFacetFilters } from "@/facets/use-facet-filters";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { useRecordView } from "@/hooks/use-record-view";
import { useTextInputFilter } from "@/hooks/use-text-input-filter";
import {
  ARTIST_DISCOGRAPHY_URL_CONFIG,
  buildFacetedSearchState,
  writeFacetedSearchState,
} from "@/lib/faceted-search-url";
import { buildFilterLinkTarget } from "@/lib/filter-url-builder";
import { getSingleReleaseSearchFormat } from "@/lib/release-search-format-filter";
import type { DiscographyQueryType } from "@/types/discography";
import { orpc } from "@/utils/orpc.client";
import type { FacetedSearchState } from "@garimpa/lib/searchState";

import { ArtistAbout } from "./artist-about";
import { getArtistBioResourceQueryOptions } from "./artist-bio-resource-query";
import { ArtistDiscography, type SortOption } from "./artist-discography";
import { ArtistHeader } from "./artist-header";
import { getArtistQueryOptions } from "./artist-query";
import { useDiscographyQueryType } from "./use-discography-query-type";

interface ArtistContentProps {
  id: string;
  /** Matches SSR prefetch: URL `type` or `auto` when param absent. */
  discographyQueryType: DiscographyQueryType;
}
const routeApi = getRouteApi("/_app/artist/$id");

export function ArtistContent({
  id,
  discographyQueryType: serverDiscographyQueryType,
}: ArtistContentProps) {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const facetState = buildFacetedSearchState(search, ARTIST_DISCOGRAPHY_URL_CONFIG);
  const activeFormat = defaultTo(getSingleReleaseSearchFormat(get(facetState, "filter")), "all");
  const onSearchStateChange = useCallback(
    (next: FacetedSearchState | undefined) => {
      navigate({
        search: (prev) => writeFacetedSearchState(prev, next, ARTIST_DISCOGRAPHY_URL_CONFIG),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );
  const facetControls = useFacetFilters({
    state: facetState,
    onChange: onSearchStateChange,
    surface: ARTIST_DISCOGRAPHY_SURFACE,
  });
  const updateFilter = useCallback(
    (key: string, value: string | undefined) => {
      navigate({
        search: (prev) => {
          if (key === "q") return { ...prev, q: value || undefined };
          if (key === "type") return { ...prev, type: value || undefined };
          return prev;
        },
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );
  const getDiscographyTypeLinkTarget = useCallback(
    (value: string) =>
      buildFilterLinkTarget({
        to: "/artist/$id",
        params: { id },
        currentSearch: search,
        target: { key: "type", kind: "param", value },
      }),
    [id, search],
  );
  const getDiscographyFormatLinkTarget = useCallback(
    (value: string) =>
      buildFilterLinkTarget({
        to: "/artist/$id",
        params: { id },
        currentSearch: search,
        target:
          value === "all"
            ? {
                config: ARTIST_DISCOGRAPHY_URL_CONFIG,
                key: "format",
                mode: "clear",
              }
            : {
                config: ARTIST_DISCOGRAPHY_URL_CONFIG,
                key: "format",
                mode: "toggle",
                value,
              },
      }),
    [id, search],
  );
  const sort = facetControls.sort as SortOption;
  const discographyQueryType = useDiscographyQueryType(search.type, serverDiscographyQueryType);
  const searchFilter = useTextInputFilter("q", defaultTo(search.q, ""), updateFilter);

  const t = useTranslations("artist");
  const tc = useTranslations("globals.common");
  const locale = useLocale();

  useRecordView("artist", id);

  const artistQuery = useQuery(getArtistQueryOptions({ id, locale }));
  const computeArtistBioResource = useCallback(
    () => orpc.music.computeArtistBioResource.call({ artistId: id, locale }),
    [id, locale],
  );
  const { value: artistBio } = useDeferredResourceQuery({
    queryOptions: getArtistBioResourceQueryOptions({
      artistId: id,
      locale,
    }),
    compute: computeArtistBioResource,
  });

  const artist = defaultTo(artistQuery.data, null);
  const isLoading = artistQuery.isLoading;

  if (isLoading) {
    return <Spinner variant="page" />;
  }

  if (artistQuery.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ErrorIcon className="text-6xl text-destructive mb-4" />
          <p className="text-xl font-medium">{t("errorLoading")}</p>
          <p className="text-muted-foreground mt-2">{artistQuery.error.message}</p>
          <Link to="/search" className="mt-4 inline-block text-primary hover:underline">
            {tc("backToSearch")}
          </Link>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <PersonUnavailableIcon className="text-6xl text-muted-foreground mb-4" />
          <p className="text-xl font-medium">{t("notFound")}</p>
          <Link to="/search" className="mt-4 inline-block text-primary hover:underline">
            {tc("backToSearch")}
          </Link>
        </div>
      </div>
    );
  }

  const bio = defaultTo(artistBio, artist.bio);
  const officialUrls = defaultTo(artist.officialUrls, []);
  const socialUrls = defaultTo(artist.socialUrls, []);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {artist.backgroundUrl && (
        <div className="absolute inset-x-0 top-0 h-[320px] overflow-hidden -z-0">
          <HeroBackgroundImage
            imageUrl={artist.backgroundUrl}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>
      )}

      <article className="relative max-w-[1440px] mx-auto px-4 md:px-10 lg:px-20 pt-5 pb-8 md:pt-6">
        <ArtistHeader
          artistId={id}
          name={artist.name}
          type={artist.type}
          ended={artist.ended}
          beginYear={artist.beginYear}
          endYear={artist.endYear}
          realname={artist.realname}
          country={artist.country}
          originAreaName={artist.originAreaName}
          imageUrl={artist.imageUrl}
          bioText={defaultTo(get(bio, "text"), null)}
          bioHtml={defaultTo(get(bio, "html"), null)}
          wikidataId={artist.wikidataId}
          officialUrls={officialUrls}
          socialUrls={socialUrls}
          genres={artist.genres}
          styles={artist.styles}
          taxonomyPages={artist.taxonomyPages}
          primaryIndex={artist.primaryIndex}
          aside={
            <ArtistAbout
              artistId={id}
              artistName={artist.name}
              members={defaultTo(artist.members, [])}
              officialUrls={officialUrls}
              socialUrls={socialUrls}
              otherUrls={defaultTo(artist.otherUrls, [])}
            />
          }
        />

        <ArtistDiscography
          artistId={id}
          discographyQueryType={discographyQueryType}
          format={activeFormat}
          sort={sort}
          facetControls={facetControls}
          searchValue={searchFilter.value}
          onSearchChange={searchFilter.setValue}
          updateFilter={updateFilter}
          getTypeLinkTarget={getDiscographyTypeLinkTarget}
          getFormatLinkTarget={getDiscographyFormatLinkTarget}
        />

        <AlertsCallout surface="artist_detail" />

        <FeaturePageBacklinks entityType="artist" catalogId={id} />
      </article>
    </div>
  );
}
